import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	FormGroup,
	FormControlLabel,
	Typography,
	LinearProgress,
	ToggleButton,
	ToggleButtonGroup,
	InputAdornment,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AddDialogStyle from "styles/application/AddDialogStyle";
import schema from "./schema";
import {
	postQuestions,
	patchQuestions,
} from "services/models/modelDetails/modelTasks/questions";
import { saveConditionMonitoringAlert } from "services/analytics";
import { FULL_ACCESS, EDIT_ACCESS } from "constants/AccessTypes/AccessTypes";
import { THRESHOLD_MODE } from "helpers/conditionMonitoringAlert";
import { generateErrorState, handleValidateObj } from "helpers/utils";
import Dropdown from "components/Elements/Dropdown";
import ErrorInputFieldWrapper from "components/Layouts/ErrorInputFieldWrapper";
import EMICheckbox from "components/Elements/EMICheckbox";
import ListAnswers from "./ListAnswers";

const questionTypeOptions = [
	{ label: "Checkbox", value: "B" },
	{ label: "Checkbox List", value: "C" },
	{ label: "Date", value: "D" },
	{ label: "Dropdown", value: "O" },
	{ label: "Long Text", value: "L" },
	{ label: "Number", value: "N" },
	{ label: "Short Text", value: "S" },
	{ label: "Time", value: "T" },
];

const ADD = AddDialogStyle();

const useStyles = makeStyles()((theme) => ({
	paper: { minWidth: "90%" },
	numberFields: { display: "flex", width: "100%", gap: 5, flexWrap: "wrap" },
	alertSection: {
		width: "100%",
		display: "flex",
		flexDirection: "column",
	},
	alertToggle: { marginBottom: 15 },
	alertValueFields: {
		display: "flex",
		width: "100%",
		gap: 10,
		flexWrap: "wrap",
		marginBottom: 15,
	},
}));

const initialState = {
	caption: "",
	type: "",
	decimalPlaces: 0, // 0 should be the default -- EMI3FE-144
	minValue: null,
	maxValue: null,
	checkboxCaption: "",
	captureImage: false,
	isCompulsory: true,
	options: null,
	enableConditionMonitoringAlert: false,
	alertThresholdMode: THRESHOLD_MODE.PERCENTAGE,
	alertAboveThreshold: null,
	alertBelowThreshold: null,
	alertMessage: "",
};

const defaultError = {
	caption: null,
	type: null,
	decimalPlaces: null,
	minValue: null,
	maxValue: null,
	checkboxCaption: null,
	captureImage: null,
	isCompulsory: null,
	options: null,
	alertAboveThreshold: null,
	alertBelowThreshold: null,
	alertMessage: null,
};

function AddEditModel({
	open,
	questionDetail,
	handleClose,
	title,
	taskId,
	handleAddEditComplete,
	getError,
	addEditType,
	isReadOnly,
	access,
	allowConditionMonitoringDataAlert,
}) {
	const { classes, cx } = useStyles();

	// Editing the Rate of Change alert config requires Model Full or Edit access.
	const canEditAlert = access === FULL_ACCESS || access === EDIT_ACCESS;

	const [input, setInput] = useState(initialState);
	const [errors, setErrors] = useState(defaultError);
	const [loading, setLoading] = useState(false);

	const setState = (value) => setInput((th) => ({ ...th, ...value }));

	// On a published (read-only) model the question fields stay locked, but the
	// Condition Monitoring alert config remains editable and is saved via the
	// analytics endpoint (which updates the active and development versions).
	const alertOnlyEditable =
		isReadOnly &&
		allowConditionMonitoringDataAlert &&
		input.type === "N" &&
		canEditAlert;

	useEffect(() => {
		// If Edit Mode
		if (questionDetail && addEditType === "edit") {
			const {
				caption,
				captureImage,
				checkboxCaption,
				decimalPlaces,
				isCompulsory,
				maxValue,
				minValue,
				type,
				options,
				enableConditionMonitoringAlert,
				alertThresholdMode,
				alertAboveThreshold,
				alertBelowThreshold,
				alertMessage,
			} = questionDetail;
			setInput({
				caption,
				captureImage: !!captureImage,
				checkboxCaption,
				decimalPlaces,
				isCompulsory,
				maxValue,
				minValue,
				type,
				options: options?.map((x) => ({
					name: x.name,
					raiseDefect: x.raiseDefect,
				})),
				enableConditionMonitoringAlert: !!enableConditionMonitoringAlert,
				alertThresholdMode: alertThresholdMode ?? THRESHOLD_MODE.PERCENTAGE,
				alertAboveThreshold: alertAboveThreshold ?? null,
				alertBelowThreshold: alertBelowThreshold ?? null,
				alertMessage: alertMessage ?? "",
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [questionDetail]);

	const handleType = (val) => {
		setState({ type: val.value });
	};

	const handleAddTaskQuestion = async (data) => {
		data["ModelVersionTaskID"] = taskId;
		setLoading(true);
		try {
			let result = await postQuestions({
				...data,
			});
			if (result.status) {
				handleAddEditComplete();
			} else {
				getError(result.data.detail || "Could not add task question");
			}
		} catch (e) {
			getError("Could not add task question");
		} finally {
			setLoading(false);
		}
	};

	const handleEditTaskQuestion = async (data) => {
		const main = Object.entries(data).map(([key, value]) => ({
			op: "replace",
			path: key,
			value: value,
		}));

		setLoading(true);
		try {
			let result = await patchQuestions(
				questionDetail.id,
				main.filter((x) => x.path !== "options")
			);
			if (result.status) {
				handleAddEditComplete();
			} else {
				getError(result.data.detail || "Could not edit task question");
			}
		} catch (e) {
			getError("Could not edit task question");
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		const d = { ...input };

		if (input.type === "B") {
			// For Checkbox Type
			d["decimalPlaces"] = null;
			d["maxValue"] = null;
			d["minValue"] = null;
			d["options"] = [];
		} else if (input.type === "N") {
			// For Number Type
			d["checkboxCaption"] = "";
			d["options"] = [];
		} else if (input.type === "C" || input.type === "O") {
			d["checkboxCaption"] = "";
			d["decimalPlaces"] = null;
		} else {
			d["decimalPlaces"] = null;
			d["maxValue"] = null;
			d["minValue"] = null;
			d["checkboxCaption"] = "";
			d["options"] = [];
		}

		// Condition Monitoring alert only applies to Number questions while the
		// SiteApp allows it; otherwise reset the alert config so stale values
		// aren't persisted.
		if (
			input.type !== "N" ||
			!allowConditionMonitoringDataAlert ||
			!input.enableConditionMonitoringAlert
		) {
			d["enableConditionMonitoringAlert"] = false;
			d["alertThresholdMode"] = THRESHOLD_MODE.PERCENTAGE;
			d["alertAboveThreshold"] = null;
			d["alertBelowThreshold"] = null;
			d["alertMessage"] = "";
		} else {
			d["alertAboveThreshold"] =
				d["alertAboveThreshold"] === "" ? null : d["alertAboveThreshold"];
			d["alertBelowThreshold"] =
				d["alertBelowThreshold"] === "" ? null : d["alertBelowThreshold"];

			if (d["alertAboveThreshold"] == null && d["alertBelowThreshold"] == null) {
				setErrors({
					...defaultError,
					alertAboveThreshold: "Set an Above or Below threshold",
				});
				return;
			}
		}

		try {
			const localChecker = await handleValidateObj(
				schema(
					input.type,
					input.type === "N" &&
						allowConditionMonitoringDataAlert &&
						input.enableConditionMonitoringAlert
				),
				{
				...d,
				options:
					d?.options?.map((x) => ({
						name: x.name,
						raiseDefect: x.raiseDefect,
					})) || [],
			});
			if (!localChecker.some((el) => el.valid === false)) {
				setErrors(defaultError);
				if (questionDetail) {
					handleEditTaskQuestion(d);
				} else {
					handleAddTaskQuestion({
						...d,
						options:
							d?.options?.map((x) => ({
								name: x.name,
								raiseDefect: x.raiseDefect || false,
							})) || [],
					});
				}
			} else {
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
			}
		} catch (e) {
			return;
		}
	};

	const handleSaveAlert = async () => {
		const enabled = input.enableConditionMonitoringAlert;
		const toNum = (v) => (v === null || v === "" ? null : parseFloat(v));

		// Validate the alert fields (message required when enabled, thresholds numeric)
		// using the same schema as the full question save.
		if (enabled) {
			const alertValidation = {
				alertAboveThreshold:
					input.alertAboveThreshold === "" ? null : input.alertAboveThreshold,
				alertBelowThreshold:
					input.alertBelowThreshold === "" ? null : input.alertBelowThreshold,
				alertMessage: input.alertMessage,
			};
			const localChecker = await handleValidateObj(
				schema(input.type, true),
				alertValidation
			);
			if (localChecker.some((el) => el.valid === false)) {
				setErrors({ ...defaultError, ...generateErrorState(localChecker) });
				return;
			}
			if (
				alertValidation.alertAboveThreshold == null &&
				alertValidation.alertBelowThreshold == null
			) {
				setErrors({
					...defaultError,
					alertAboveThreshold: "Set an Above or Below threshold",
				});
				return;
			}
		}

		setErrors(defaultError);
		setLoading(true);
		try {
			const result = await saveConditionMonitoringAlert({
				modelVersionTaskQuestionID: parseInt(questionDetail.id),
				enableConditionMonitoringAlert: enabled,
				alertThresholdMode: enabled
					? input.alertThresholdMode
					: THRESHOLD_MODE.PERCENTAGE,
				alertAboveThreshold: enabled ? toNum(input.alertAboveThreshold) : null,
				alertBelowThreshold: enabled ? toNum(input.alertBelowThreshold) : null,
				alertMessage: enabled ? input.alertMessage : "",
			});
			if (result.status) {
				handleAddEditComplete();
			} else {
				getError(result.data?.detail || "Could not save alert configuration");
			}
		} catch (e) {
			getError("Could not save alert configuration");
		} finally {
			setLoading(false);
		}
	};

	const handleEnterPress = (e) => {
		if (e.keyCode === 13) {
			handleSave();
		}
	};

	const closeOverride = () => {
		handleClose();
	};

	return (
		<Dialog
			classes={{ paper: classes.paper }}
			open={open}
			onClose={closeOverride}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			{loading ? <LinearProgress /> : null}
			<ADD.ActionContainer>
				<DialogTitle id="alert-dialog-title">
					<ADD.HeaderText>
						{isReadOnly ? "View" : questionDetail ? "Edit" : "Add"} {title}
					</ADD.HeaderText>
				</DialogTitle>

				<ADD.ButtonContainer>
					{isReadOnly ? (
						alertOnlyEditable ? (
							<>
								<ADD.CancelButton onClick={closeOverride} variant="contained">
									Cancel
								</ADD.CancelButton>
								<ADD.ConfirmButton variant="contained" onClick={handleSaveAlert}>
									Save Alert
								</ADD.ConfirmButton>
							</>
						) : (
							<ADD.ConfirmButton variant="contained" onClick={closeOverride}>
								Close
							</ADD.ConfirmButton>
						)
					) : (
						<>
							<ADD.CancelButton onClick={closeOverride} variant="contained">
								Cancel
							</ADD.CancelButton>
							<ADD.ConfirmButton variant="contained" onClick={handleSave}>
								{questionDetail ? "Close" : "Add " + title}
							</ADD.ConfirmButton>
						</>
					)}
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<ADD.DialogContent>
				<ADD.InputContainer>
					<ADD.LeftInputContainer>
						<ADD.NameLabel>
							Caption<ADD.RequiredStar>*</ADD.RequiredStar>
						</ADD.NameLabel>
						<ADD.NameInput
							error={errors.caption === null ? false : true}
							helperText={errors.caption === null ? null : errors.caption}
							variant="outlined"
							size="medium"
							value={input.caption}
							autoFocus
							disabled={isReadOnly}
							onKeyDown={handleEnterPress}
							onChange={(e) => {
								setState({ caption: e.target.value });
							}}
						/>
					</ADD.LeftInputContainer>
					<ADD.RightInputContainer>
						<ADD.NameLabel>
							Question Type<ADD.RequiredStar>*</ADD.RequiredStar>
						</ADD.NameLabel>
						<ErrorInputFieldWrapper errorMessage={errors.type}>
							<Dropdown
								options={questionTypeOptions}
								selectedValue={questionTypeOptions.find(
									(x) => x.value === input.type
								)}
								width="100%"
								onChange={handleType}
								disabled={isReadOnly}
							/>
						</ErrorInputFieldWrapper>
					</ADD.RightInputContainer>
				</ADD.InputContainer>
				<ADD.InputContainer>
					<ADD.LeftInputContainer>
						<FormGroup row>
							<FormControlLabel
								style={{ marginLeft: 0, marginTop: -20 }}
								control={
									<EMICheckbox
										state={input.isCompulsory}
										disabled={isReadOnly}
										changeHandler={() => {
											setState({
												isCompulsory: !input.isCompulsory,
											});
										}}
									/>
								}
								label={<Typography>Compulsory</Typography>}
							/>
							<FormControlLabel
								style={{ marginLeft: 16, marginTop: -20 }}
								control={
									<EMICheckbox
										state={input.captureImage}
										disabled={isReadOnly}
										changeHandler={() => {
											setState({
												captureImage: !input.captureImage,
											});
										}}
									/>
								}
								label={<Typography>Capture an image</Typography>}
							/>
							{input.type === "N" && allowConditionMonitoringDataAlert ? (
								<FormControlLabel
									style={{ marginLeft: 16, marginTop: -20 }}
									control={
										<EMICheckbox
											state={input.enableConditionMonitoringAlert}
											disabled={!canEditAlert}
											changeHandler={() => {
												setState({
													enableConditionMonitoringAlert:
														!input.enableConditionMonitoringAlert,
												});
											}}
										/>
									}
									label={<Typography>Alert</Typography>}
								/>
							) : null}
						</FormGroup>
					</ADD.LeftInputContainer>
				</ADD.InputContainer>
				<ADD.InputContainer>
					{input.type === "B" ? (
						<ADD.LeftInputContainer>
							<ADD.NameLabel>
								Checkbox Caption<ADD.RequiredStar>*</ADD.RequiredStar>
							</ADD.NameLabel>
							<ADD.NameInput
								error={errors.checkboxCaption === null ? false : true}
								helperText={
									errors.checkboxCaption === null
										? null
										: errors.checkboxCaption
								}
								variant="outlined"
								size="medium"
								value={input.checkboxCaption}
								autoFocus
								disabled={isReadOnly}
								onKeyDown={handleEnterPress}
								onChange={(e) => {
									setState({ checkboxCaption: e.target.value });
								}}
							/>
						</ADD.LeftInputContainer>
					) : input.type === "N" ? (
						<div className={classes.numberFields}>
							<div style={{ flexBasis: "33%" }}>
								<ADD.NameLabel>
									Decimal Places<ADD.RequiredStar>*</ADD.RequiredStar>
								</ADD.NameLabel>
								<ADD.NameInput
									type="number"
									error={errors.decimalPlaces === null ? false : true}
									helperText={
										errors.decimalPlaces === null ? null : errors.decimalPlaces
									}
									variant="outlined"
									size="medium"
									value={input.decimalPlaces}
									autoFocus
									disabled={isReadOnly}
									onKeyDown={handleEnterPress}
									onBlur={() => {
										// 0 should be the default -- EMI3FE-144
										if (!input?.decimalPlaces) {
											setState({ decimalPlaces: "0" });
										}
									}}
									onChange={(e) => {
										setState({ decimalPlaces: e.target.value });
									}}
								/>
							</div>
							<div style={{ flexBasis: "33%" }}>
								<ADD.NameLabel>Minimum Value</ADD.NameLabel>
								<ADD.NameInput
									type="number"
									error={errors.minValue === null ? false : true}
									helperText={errors.minValue === null ? null : errors.minValue}
									variant="outlined"
									size="medium"
									value={input.minValue}
									disabled={isReadOnly}
									onKeyDown={handleEnterPress}
									onChange={(e) => {
										setState({
											minValue: e.target.value === "" ? null : e.target.value,
										});
									}}
								/>
							</div>
							<div style={{ flexBasis: "33%" }}>
								<ADD.NameLabel>Maximum Value</ADD.NameLabel>
								<ADD.NameInput
									type="number"
									error={errors.maxValue === null ? false : true}
									helperText={errors.maxValue === null ? null : errors.maxValue}
									variant="outlined"
									size="medium"
									value={input.maxValue}
									disabled={isReadOnly}
									onKeyDown={handleEnterPress}
									onChange={(e) => {
										setState({
											maxValue: e.target.value === "" ? null : e.target.value,
										});
									}}
								/>
							</div>
						</div>
					) : input.type === "C" || input.type === "O" ? (
						isReadOnly ? (
							<ADD.LeftInputContainer>
								<ADD.NameLabel>
									{input.type === "O" ? "Dropdown" : "Checkbox"} List Options
								</ADD.NameLabel>
								{input.options?.length ? (
									input.options.map((o, i) => (
										<Typography key={i} className={classes.checkboxText}>
											{o.name}
											{o.raiseDefect ? " (raises defect)" : ""}
										</Typography>
									))
								) : (
									<Typography className={classes.checkboxText}>
										No options
									</Typography>
								)}
							</ADD.LeftInputContainer>
						) : (
							<ListAnswers
								type={input.type}
								modelVersionTaskQuestionID={questionDetail?.id}
								getError={getError}
								addEditType={addEditType}
								handleUpdateOption={setInput}
								inputOptions={input?.options}
								errors={errors}
							/>
						)
					) : null}
				</ADD.InputContainer>
				{input.type === "N" &&
				allowConditionMonitoringDataAlert &&
				input.enableConditionMonitoringAlert ? (
					<ADD.InputContainer>
						<div className={classes.alertSection}>
							<ADD.NameLabel>Threshold Mode</ADD.NameLabel>
							<ToggleButtonGroup
								className={classes.alertToggle}
								exclusive
								color="primary"
								disabled={!canEditAlert}
								value={input.alertThresholdMode}
								onChange={(e, val) => {
									// ToggleButtonGroup returns null when the active
									// button is re-clicked; keep the current selection.
									if (val !== null) {
										setState({ alertThresholdMode: val });
									}
								}}
							>
								<ToggleButton value={THRESHOLD_MODE.PERCENTAGE}>
									Percentage (%)
								</ToggleButton>
								<ToggleButton value={THRESHOLD_MODE.ABSOLUTE}>
									Absolute
								</ToggleButton>
							</ToggleButtonGroup>
							<div className={classes.alertValueFields}>
								<div style={{ flexBasis: "48%" }}>
									<ADD.NameLabel>Above Threshold</ADD.NameLabel>
									<ADD.NameInput
										type="number"
										error={errors.alertAboveThreshold !== null}
										helperText={errors.alertAboveThreshold}
										variant="outlined"
										size="medium"
										value={input.alertAboveThreshold ?? ""}
										disabled={!canEditAlert}
										onKeyDown={handleEnterPress}
										InputProps={
											input.alertThresholdMode === THRESHOLD_MODE.PERCENTAGE
												? {
														endAdornment: (
															<InputAdornment position="end">%</InputAdornment>
														),
												  }
												: undefined
										}
										onChange={(e) => {
											setState({
												alertAboveThreshold:
													e.target.value === "" ? null : e.target.value,
											});
										}}
									/>
								</div>
								<div style={{ flexBasis: "48%" }}>
									<ADD.NameLabel>Below Threshold</ADD.NameLabel>
									<ADD.NameInput
										type="number"
										error={errors.alertBelowThreshold !== null}
										helperText={errors.alertBelowThreshold}
										variant="outlined"
										size="medium"
										value={input.alertBelowThreshold ?? ""}
										disabled={!canEditAlert}
										onKeyDown={handleEnterPress}
										InputProps={
											input.alertThresholdMode === THRESHOLD_MODE.PERCENTAGE
												? {
														endAdornment: (
															<InputAdornment position="end">%</InputAdornment>
														),
												  }
												: undefined
										}
										onChange={(e) => {
											setState({
												alertBelowThreshold:
													e.target.value === "" ? null : e.target.value,
											});
										}}
									/>
								</div>
							</div>
							<ADD.NameLabel>
								Alert Message<ADD.RequiredStar>*</ADD.RequiredStar>
							</ADD.NameLabel>
							<ADD.NameInput
								error={errors.alertMessage !== null}
								helperText={errors.alertMessage}
								variant="outlined"
								size="medium"
								multiline
								minRows={2}
								value={input.alertMessage ?? ""}
								disabled={!canEditAlert}
								onChange={(e) => {
									setState({ alertMessage: e.target.value });
								}}
							/>
						</div>
					</ADD.InputContainer>
				) : null}
			</ADD.DialogContent>
		</Dialog>
	);
}

export default AddEditModel;
